/**
 * Responsibility: one row per image library - what we hold, and where it is.
 *
 * Always visible, because this is the question the tab exists to answer.
 * Everything else on the tab is a breakdown of it and is collapsed.
 *
 * **Held is always written against what exists.** 26 of 7,127 says
 * something; 26 does not. Where nobody publishes a total - eBay does not -
 * the share is a dash rather than 0%, because a zero reads as "we have none
 * of it" and the truth is "there is no denominator".
 *
 * **On-disk is shown beside the image count, not instead of it.** The gap
 * between them is how much has already moved to Nextcloud, and the archive
 * verifies an upload before deleting anything, so the gap means moved and
 * not lost.
 *
 * Where it lives is two symbols rather than a phrase, the same pair the
 * Index tab uses: a server and a cloud, blue when present and struck
 * through when not. "box + cloud" was a word to be read for a question
 * that is really two yes-or-nos.
 */
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { DataHubSet } from '../../api/client';
import { heldOf, readableBytes, shareLabel, stamp } from './dataHubFormat';
import { LocationIcons } from './StorageIcons';

type DataHubSetsProps = { sets: DataHubSet[] };

export function DataHubSets({ sets }: DataHubSetsProps) {
  if (sets.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No image library has been harvested yet.
      </Typography>
    );
  }

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={{ pl: 0 }}>Source</TableCell>
          <TableCell align="right">Held</TableCell>
          <TableCell align="right">Images</TableCell>
          <TableCell align="right">Size</TableCell>
          <TableCell>Where</TableCell>
          <TableCell align="right" sx={{ pr: 0 }}>
            Last updated
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {sets.map((row) => (
          <TableRow key={row.name}>
            <TableCell sx={{ pl: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {row.name}
              </Typography>
            </TableCell>

            <TableCell align="right" sx={{ minWidth: 150 }}>
              <Stack spacing={0.5} alignItems="flex-end">
                <Typography variant="body2">
                  {heldOf(row.held, row.available)}
                  <Typography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                    sx={{ ml: 0.75 }}
                  >
                    {shareLabel(row.available, row.share)}
                  </Typography>
                </Typography>
                {row.available === null ? (
                  <Tooltip title="Nobody publishes how many of these exist, so there is no total to measure against.">
                    <Typography variant="caption" color="text.secondary">
                      no published total
                    </Typography>
                  </Tooltip>
                ) : (
                  <LinearProgress
                    variant="determinate"
                    // Always visible: a real share here is a fraction of a
                    // percent, and an empty bar looks broken rather than small.
                    value={Math.max(Math.min(row.share * 100, 100), 0.5)}
                    sx={{ height: 5, borderRadius: 1, width: '100%' }}
                  />
                )}
              </Stack>
            </TableCell>

            <TableCell align="right">
              <Typography variant="body2">{row.images.toLocaleString()}</Typography>
              <Typography variant="caption" color="text.secondary">
                {row.on_disk === row.images
                  ? 'all on the box'
                  : `${row.on_disk.toLocaleString()} on the box`}
              </Typography>
            </TableCell>

            <TableCell align="right">{readableBytes(row.bytes)}</TableCell>

            <TableCell>
              <LocationIcons location={row.location} />
            </TableCell>

            <TableCell align="right" sx={{ pr: 0, whiteSpace: 'nowrap' }}>
              <Typography variant="body2" color="text.secondary">
                {stamp(row.last_updated)}
              </Typography>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
